import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { IGlobalStoreReducers } from './ReduxStore';
import { PdfViewerViewModeFlag } from '../pdf/Enums';
import type { ITextDialogAttributes, IPresetLists, IPresetSelections } from '../types';
import { GlobalConstants } from '../pdf';

export interface IEplassState {
    autoSave: boolean;
    documentUrl: string;
    documentUrl2: string;
    documentName: string;
    documentVersion: string;
    documentSubject: string;
    isSavingChanges: boolean;
    isDocumentSaved: boolean;
    lists: IPresetLists;
    selections: IPresetSelections;
    viewMode: PdfViewerViewModeFlag;
    ribbonRefresh: number;
    annotSidepanelRefresh: number;
    userSettingsDialogRefresh: number;
    undoRedoRefresh: number;
}

export const defaultMeasureRatio = {
    userSpaceValue: 1,
    userSpaceUnit: 'cm',
    realValue: 1,
    realUnit: 'm',
};

const initialState: IEplassState = {
    autoSave: false,
    isSavingChanges: false,
    isDocumentSaved: true,
    lists: {},
    selections: {
        color: 1,
        fillColor: 0,
        textAttributes: {
            align: [true, false, false, false],
            bold: false,
            family: GlobalConstants.default.fontFamily,
            italic: false,
            size: GlobalConstants.default.fontSize,
            strikeout: false,
            underline: false,
        },
        lineWidth: 2.0,
        lineStyle: 'solid',
        lineCloudyStyle: 'cloudy small',
        arrowStart: 'none',
        arrowEnd: 'openArrow',
        lineStart: 'none',
        lineEnd: 'none',
        stampName: 'Approved',
        measureRatio: defaultMeasureRatio,
    },
    viewMode: PdfViewerViewModeFlag.Loading,
    ribbonRefresh: -1,
    annotSidepanelRefresh: -1,
    userSettingsDialogRefresh: -1,
    undoRedoRefresh: -1,
    documentName: '',
    documentUrl: '',
    documentUrl2: '',
    documentVersion: '',
    documentSubject: '',
};

const reducers = {
    update: (
        state: IEplassState,
        action: PayloadAction<{
            documentUrl?: string;
            viewMode?: PdfViewerViewModeFlag;
        }>,
    ) => {
        if (action.payload.viewMode) {
            state.viewMode = action.payload.viewMode;
        }
        if (action.payload.documentUrl) {
            state.documentUrl = action.payload.documentUrl;
        }
    },
    changeViewMode: (state: IEplassState, action: PayloadAction<PdfViewerViewModeFlag>) => {
        if (action.payload === PdfViewerViewModeFlag.View) {
            state.autoSave = false;
        }
        state.viewMode = action.payload;
    },
    changeDocumentUrl: (state: IEplassState, action: PayloadAction<string>) => {
        state.documentUrl = action.payload;
        state.documentUrl2 = '';
    },
    changeDocumentsUrl: (state: IEplassState, action: PayloadAction<[string, string]>) => {
        state.documentUrl = action.payload[0];
        state.documentUrl2 = action.payload[1];
    },
    changeDocumentName: (state: IEplassState, action: PayloadAction<string>) => {
        state.documentName = action.payload;
    },
    changeDocumentVersion: (state: IEplassState, action: PayloadAction<string>) => {
        state.documentVersion = action.payload;
    },
    changeDocumentSubject: (state: IEplassState, action: PayloadAction<string>) => {
        state.documentSubject = action.payload;
    },
    changeLists: (state: IEplassState, action: PayloadAction<IPresetLists>) => {
        state.lists = action.payload;
    },
    changeSelections: (state: IEplassState, action: PayloadAction<IPresetSelections>) => {
        // ToDO: if (state.selections.stampName !== action.payload.stampName) {
        //     GlobalVariables.stampImageScale = 1.0;
        //     GlobalVariables.stampImageUseZoomFactor = false;
        // }
        state.selections = action.payload;
    },
    changeSelectedColor: (state: IEplassState, action: PayloadAction<number>) => {
        const updateValue = { ...state.selections };
        updateValue.color = action.payload;
        state.selections = updateValue;
    },
    changeSelectedFillColor: (state: IEplassState, action: PayloadAction<number>) => {
        const updateValue = { ...state.selections };
        updateValue.fillColor = action.payload;
        state.selections = updateValue;
    },
    changeSelectedFontAttributes: (
        state: IEplassState,
        action: PayloadAction<ITextDialogAttributes>,
    ) => {
        const updateValue = { ...state.selections };
        updateValue.textAttributes = action.payload;
        state.selections = updateValue;
    },
    changeSelectedLineWidth: (state: IEplassState, action: PayloadAction<number>) => {
        const updateValue = { ...state.selections };
        updateValue.lineWidth = action.payload;
        state.selections = updateValue;
    },
    changeSelectedLineStyle: (state: IEplassState, action: PayloadAction<string>) => {
        const updateValue = { ...state.selections };
        updateValue.lineStyle = action.payload;
        state.selections = updateValue;
    },
    changeSelectedLineCloudyStyle: (state: IEplassState, action: PayloadAction<string>) => {
        const updateValue = { ...state.selections };
        updateValue.lineCloudyStyle = action.payload;
        state.selections = updateValue;
    },
    changeSelectedArrowStart: (state: IEplassState, action: PayloadAction<string>) => {
        const updateValue = { ...state.selections };
        updateValue.arrowStart = action.payload;
        state.selections = updateValue;
    },
    changeSelectedArrowEnd: (state: IEplassState, action: PayloadAction<string>) => {
        const updateValue = { ...state.selections };
        updateValue.arrowEnd = action.payload;
        state.selections = updateValue;
    },
    changeSelectedLineStart: (state: IEplassState, action: PayloadAction<string>) => {
        const updateValue = { ...state.selections };
        updateValue.lineStart = action.payload;
        state.selections = updateValue;
    },
    changeSelectedLineEnd: (state: IEplassState, action: PayloadAction<string>) => {
        const updateValue = { ...state.selections };
        updateValue.lineEnd = action.payload;
        state.selections = updateValue;
    },
    changeSelectedStamp: (state: IEplassState, action: PayloadAction<string>) => {
        // ToDO: GlobalVariables.stampImageScale = 1.0;
        // GlobalVariables.stampImageUseZoomFactor = false;
        const updateValue = { ...state.selections };
        updateValue.stampName = action.payload;
        state.selections = updateValue;
    },
    changeUserAutosave: (state: IEplassState, action: PayloadAction<boolean>) => {
        state.autoSave = action.payload;
    },
    changeDocumentSaved: (state: IEplassState, action: PayloadAction<boolean>) => {
        state.isDocumentSaved = action.payload;
    },
    changeDocumentSavingChanges: (state: IEplassState, action: PayloadAction<boolean>) => {
        state.isSavingChanges = action.payload;
    },
    forceRibbonRefresh: (state: IEplassState) => {
        state.ribbonRefresh = Math.round(Math.random() * 1000000);
    },
    forceAnnotSidepanelRefresh: (state: IEplassState) => {
        state.annotSidepanelRefresh = Math.round(Math.random() * 1000000);
    },
    forceUserSettingsDialogRefresh: (state: IEplassState) => {
        state.userSettingsDialogRefresh = Math.round(Math.random() * 1000000);
    },
    forceUndoRedoRefresh: (state: IEplassState) => {
        state.undoRedoRefresh = Math.round(Math.random() * 1000000);
    },
};

export const eplassSlice = createSlice({
    name: 'selectedState',
    initialState,
    reducers,
});

export const eplassSliceAutoSave = (state: IGlobalStoreReducers) => state.eplassState.autoSave;
export const eplassSliceSavingChanges = (state: IGlobalStoreReducers) =>
    state.eplassState.isSavingChanges;
export const eplassSliceDocumentSaved = (state: IGlobalStoreReducers) =>
    state.eplassState.isDocumentSaved;
export const eplassSliceViewMode = (state: IGlobalStoreReducers) => state.eplassState.viewMode;
export const eplassSliceDocumentUrl = (state: IGlobalStoreReducers) =>
    state.eplassState.documentUrl;
export const eplassSliceDocumentName = (state: IGlobalStoreReducers) =>
    state.eplassState.documentName;
export const eplassSliceDocumentVersion = (state: IGlobalStoreReducers) =>
    state.eplassState.documentVersion;
export const eplassSliceDocumentSubject = (state: IGlobalStoreReducers) =>
    state.eplassState.documentSubject;
export const eplassSliceLists = (state: IGlobalStoreReducers) => state.eplassState.lists;
export const eplassSliceListColors = (state: IGlobalStoreReducers) =>
    state.eplassState.lists.colors;
export const eplassSliceListFillColors = (state: IGlobalStoreReducers) =>
    state.eplassState.lists.fillColors;
export const eplassSliceListLineWidths = (state: IGlobalStoreReducers) =>
    state.eplassState.lists.lineWidths;
export const eplassSliceListLineStyles = (state: IGlobalStoreReducers) =>
    state.eplassState.lists.lineStyles;
export const eplassSliceListLineCloudyStyles = (state: IGlobalStoreReducers) =>
    state.eplassState.lists.lineStylesCloudy;
export const eplassSliceListLineStarts = (state: IGlobalStoreReducers) =>
    state.eplassState.lists.lineStarts;
export const eplassSliceListLineEnds = (state: IGlobalStoreReducers) =>
    state.eplassState.lists.lineEnds;
export const eplassSliceListStamps = (state: IGlobalStoreReducers) =>
    state.eplassState.lists.stamps;
export const eplassSliceListUserStamps = (state: IGlobalStoreReducers) =>
    state.eplassState.lists.userStamps;
export const eplassSliceListUserUnits = (state: IGlobalStoreReducers) =>
    state.eplassState.lists.userUnits;
export const eplassSliceListRealUnits = (state: IGlobalStoreReducers) =>
    state.eplassState.lists.realUnits;
export const eplassSliceSelections = (state: IGlobalStoreReducers) => state.eplassState.selections;
export const eplassSliceColor = (state: IGlobalStoreReducers) => state.eplassState.selections.color;
export const eplassSliceFillColor = (state: IGlobalStoreReducers) =>
    state.eplassState.selections.fillColor;
export const eplassSliceLineWidth = (state: IGlobalStoreReducers) =>
    state.eplassState.selections.lineWidth;
export const eplassSliceLineStyle = (state: IGlobalStoreReducers) =>
    state.eplassState.selections.lineStyle;
export const eplassSliceCloudyLineStyle = (state: IGlobalStoreReducers) =>
    state.eplassState.selections.lineCloudyStyle;
export const eplassSliceArrowStart = (state: IGlobalStoreReducers) =>
    state.eplassState.selections.arrowStart;
export const eplassSliceArrowEnd = (state: IGlobalStoreReducers) =>
    state.eplassState.selections.arrowEnd;
export const eplassSliceLineStart = (state: IGlobalStoreReducers) =>
    state.eplassState.selections.lineStart;
export const eplassSliceLineEnd = (state: IGlobalStoreReducers) =>
    state.eplassState.selections.lineEnd;
export const eplassSliceStampName = (state: IGlobalStoreReducers) =>
    state.eplassState.selections.stampName;
export const eplassSliceMeasureRatio = (state: IGlobalStoreReducers) =>
    state.eplassState.selections.measureRatio;
export const eplassSliceState = (state: IGlobalStoreReducers) => state.eplassState.selections.state;
export const eplassSliceRibbonRefresh = (state: IGlobalStoreReducers) =>
    state.eplassState.ribbonRefresh;
export const eplassSliceAnnotationSidepanelRefresh = (state: IGlobalStoreReducers) =>
    state.eplassState.annotSidepanelRefresh;
export const eplassSliceUserSettingsDialogRefresh = (state: IGlobalStoreReducers) =>
    state.eplassState.userSettingsDialogRefresh;
export const eplassSliceUndoRedoRefresh = (state: IGlobalStoreReducers) =>
    state.eplassState.undoRedoRefresh;

export default eplassSlice.reducer;
